import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../Api/api";
import logo from "../../Assets/dbskillslogo.png"
// ─── Inline SVG icons (no extra deps) ──────────────────────────────────
const IconPerson = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconLock = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const IconEye = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconEyeOff = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const IconArrow = ({ size = 15, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);
const IconAlert = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(null);
  const [shake, setShake] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const passwordRef = useRef(null);

  // Redirect if already logged in
  useEffect(() => {
    if (localStorage.getItem("token")) navigate("/dashboard", { replace: true });
  }, []);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleLogin = async () => {
    setError("");
    if (!username.trim() || !password.trim()) {
      setError("Please fill in all fields");
      triggerShake();
      return;
    }
    try {
      setLoading(true);
      const res = await API.post("/auth/login", {
        username: username.trim(),
        password: password.trim(),
      });
      if (res.data.success) {
        localStorage.setItem("token", res.data.token);
        setFadeOut(true);
        setTimeout(() => navigate("/dashboard", { replace: true }), 300);
      } else {
        setError(res.data.message || "Invalid username or password");
        triggerShake();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please try again.");
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          background: #0A1628;
          font-family: 'Plus Jakarta Sans', sans-serif;
          transition: opacity 0.3s ease;
        }
        .login-root.fade-out { opacity: 0; }

        /* ── Top section ── */
        .login-top {
          background: linear-gradient(135deg, #0A1628 0%, #0F2040 60%, #0D1B35 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 24px 40px;
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
        }
        .deco-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(6,182,212,0.08);
          pointer-events: none;
        }
        .deco-ring-1 { width: 320px; height: 320px; top: -100px; right: -100px; }
        .deco-ring-2 { width: 200px; height: 200px; bottom: -40px; left: -60px; border-color: rgba(6,182,212,0.05); }

        .logo-outer {
          width: 88px; height: 88px;
          border-radius: 24px;
          border: 1px solid rgba(6,182,212,0.22);
          background: rgba(6,182,212,0.07);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 16px;
        }
        .logo-inner {
          width: 74px; height: 74px;
          border-radius: 19px;
          background: rgba(255,255,255,0.06);
          display: flex; align-items: center; justify-content: center;
          font-size: 28px; font-weight: 800; color: #06B6D4;
          letter-spacing: -1px;
        }
        .brand-name {
          color: #F1F5F9;
          font-size: 26px; font-weight: 800; letter-spacing: 0.3px;
          margin-bottom: 6px;
        }
        .pill {
          display: inline-flex; align-items: center; gap: 5px;
          background: rgba(6,182,212,0.08);
          border: 1px solid rgba(6,182,212,0.14);
          border-radius: 20px;
          padding: 5px 10px;
          color: #7DD3E8;
          font-size: 11px; font-weight: 600;
        }
        .pill-dot { width: 5px; height: 5px; border-radius: 50%; background: #06B6D4; }

        /* ── White card ── */
        .login-card {
          background: #fff;
          border-radius: 30px 30px 0 0;
          flex: 1;
          padding: 0 26px 40px;
          box-shadow: 0 -8px 30px rgba(0,0,0,0.15);
          display: flex;
          flex-direction: column;
        }
        .handle-bar {
          width: 44px; height: 4px;
          background: #06B6D4;
          border-radius: 2px;
          margin: 14px auto 20px;
          flex-shrink: 0;
        }
        .welcome-title {
          font-size: 22px; font-weight: 800; color: #0F172A;
          letter-spacing: -0.3px; margin-bottom: 4px;
        }
        .welcome-sub {
          font-size: 13px; font-weight: 500; color: #94A3B8;
          margin-bottom: 24px;
        }

        /* ── Fields ── */
        .field-label {
          font-size: 10px; font-weight: 800; color: #94A3B8;
          letter-spacing: 1.5px; text-transform: uppercase;
          margin-bottom: 8px; margin-left: 2px;
        }
        .input-wrap {
          display: flex; align-items: center; gap: 12px;
          background: #F8FAFC;
          border: 1.5px solid #E2E8F0;
          border-radius: 14px;
          padding: 0 16px;
          height: 52px;
          margin-bottom: 18px;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
        }
        .input-wrap.focused {
          border-color: #06B6D4;
          background: #F0FDFF;
          box-shadow: 0 0 0 3px rgba(6,182,212,0.12);
        }
        .input-wrap.has-error {
          border-color: #FCA5A5;
          background: #FFF5F5;
        }
        .input-wrap input {
          flex: 1; border: none; outline: none;
          background: transparent;
          font-size: 14px; font-weight: 500; color: #0F172A;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .input-wrap input::placeholder { color: #C0CCDA; font-weight: 400; }
        .eye-btn {
          background: none; border: none; cursor: pointer;
          display: flex; align-items: center; padding: 4px;
          color: #CBD5E1;
        }
        .eye-btn:hover { color: #94A3B8; }

        /* ── Shake animation ── */
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
        .shake { animation: shake 0.45s ease; }

        /* ── Error box ── */
        .error-box {
          display: flex; align-items: center; gap: 7px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 10px;
          padding: 9px 13px;
          margin-bottom: 12px;
          margin-top: -6px;
        }
        .error-text { color: #EF4444; font-size: 12px; font-weight: 600; flex: 1; }

        /* ── Button ── */
        .sign-btn {
          width: 100%;
          height: 54px;
          background: linear-gradient(90deg, #0F2040 0%, #1C3A6B 100%);
          border: none; border-radius: 14px;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 12px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 15px; font-weight: 800; color: #fff;
          letter-spacing: 0.3px;
          margin-top: 8px;
          box-shadow: 0 6px 20px rgba(15,32,64,0.3);
          transition: opacity 0.2s, transform 0.15s;
        }
        .sign-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
        .sign-btn:active:not(:disabled) { transform: translateY(0); }
        .sign-btn:disabled { opacity: 0.65; cursor: not-allowed; }
        .arrow-circle {
          width: 28px; height: 28px; border-radius: 50%;
          background: #fff;
          display: flex; align-items: center; justify-content: center;
        }

        /* ── Loading spinner ── */
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner {
          width: 20px; height: 20px;
          border: 2.5px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        /* ── Max-width wrapper for desktop ── */
        .login-frame {
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0A1628;
        }
        .login-phone {
          width: 100%;
          max-width: 420px;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          position: relative;
        }
        @media (min-width: 480px) {
          .login-frame { padding: 24px; }
          .login-phone {
            min-height: auto;
            border-radius: 40px;
            overflow: hidden;
            box-shadow: 0 30px 80px rgba(0,0,0,0.6);
          }
          .login-card { border-radius: 30px 30px 30px 30px; }
        }
      `}</style>

      <div className="login-frame">
        <div className={`login-phone login-root${fadeOut ? " fade-out" : ""}`}>
          {/* ── Top dark section ── */}
          <div className="login-top">
            <div className="deco-ring deco-ring-1" />
            <div className="deco-ring deco-ring-2" />

            <div className="logo-outer">
              <div className="logo-inner"><img src={logo} alt="" /></div>
            </div>

            <p className="brand-name">DB Skills</p>

            <div className="pill">
              <span className="pill-dot" />
              Attendance App
            </div>
          </div>

          {/* ── White card ── */}
          <div className="login-card">
            <div className="handle-bar" />

            <p className="welcome-title">Welcome back 👋</p>
            <p className="welcome-sub">Sign in to your account</p>

            <div className={shake ? "shake" : ""}>
              {/* Username */}
              <p className="field-label">Username</p>
              <div className={`input-wrap${focused === "username" ? " focused" : ""}${error && !username.trim() ? " has-error" : ""}`}>
                <IconPerson size={18} color={focused === "username" ? "#06B6D4" : "#CBD5E1"} />
                <input
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  autoComplete="username"
                  onChange={(e) => { setUsername(e.target.value); setError(""); }}
                  onFocus={() => setFocused("username")}
                  onBlur={() => setFocused(null)}
                  onKeyDown={(e) => e.key === "Enter" && passwordRef.current?.focus()}
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <p className="field-label">Password</p>
              <div className={`input-wrap${focused === "password" ? " focused" : ""}${error && !password.trim() ? " has-error" : ""}`}>
                <IconLock size={18} color={focused === "password" ? "#06B6D4" : "#CBD5E1"} />
                <input
                  ref={passwordRef}
                  type={showPass ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused(null)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                />
                <button className="eye-btn" type="button" onClick={() => setShowPass(v => !v)}>
                  {showPass ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="error-box">
                <IconAlert size={14} color="#EF4444" />
                <span className="error-text">{error}</span>
              </div>
            )}

            {/* Sign In */}
            <button className="sign-btn" onClick={handleLogin} disabled={loading}>
              {loading ? (
                <div className="spinner" />
              ) : (
                <>
                  Sign In
                  <div className="arrow-circle">
                    <IconArrow size={15} color="#0F2040" />
                  </div>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}